import apiV2 from "api-v2";
import type { SummaryTableConfig, SummaryTableData } from "../Submission/types/quote.type";
import type { QuoteResourceType } from "./procurer-quotes-api";

export interface CreateSummaryTablePayload {
	name: string;
	include_column_totals?: boolean;
	quote_ids: number[];
	value_column_names: string[];
}

export interface UpdateSummaryTablePayload {
	name?: string;
	include_column_totals?: boolean;
	quote_ids?: number[];
	value_column_names?: string[];
}

interface ApiResponseWithAdditional<T> {
	data: T;
	additional?: { is_quote_tab_valid?: boolean };
}

const normalizeSummaryTable = (raw: any): SummaryTableConfig => ({
	id: raw.id,
	name: raw.name,
	quote_table_ids: raw.quote_table_ids ?? raw.config?.quote_ids ?? [],
	value_column_names: raw.value_column_names ?? raw.config?.value_column_names ?? [],
	include_column_totals: raw.include_column_totals ?? raw.config?.include_column_totals ?? false,
});

export const getSummaryTables = async (
	rfxId: number,
	resourceType: QuoteResourceType = "rfx"
): Promise<SummaryTableConfig[]> => {
	const response = await apiV2.get<{ data: any[] }>(
		`${resourceType}/${rfxId}/summary-tables`
	);
	return response.data.data.map(normalizeSummaryTable);
};

export const getSummaryTableData = async (
	rfxId: number,
	summaryTableId: number,
	inviteId?: number,
	resourceType: QuoteResourceType = "rfx"
): Promise<SummaryTableData> => {
	const params = inviteId ? `?invite_id=${inviteId}` : "";
	const response = await apiV2.get<{ data: any }>(
		`${resourceType}/${rfxId}/summary-tables/${summaryTableId}/data${params}`
	);
	const raw = response.data.data;

	// The bidder path (with invite_id) returns { rows, columnTotals, grandTotal }
	// where rows[].columnTotals is a per-column map. Normalize to SummaryTableData shape.
	if (raw.rows && !raw.cells) {
		const cells: SummaryTableData["cells"] = [];
		const rowTotals: Record<number, number> = {};
		for (const row of raw.rows) {
			const qId = row.quoteId;
			rowTotals[qId] = row.rowTotal ?? 0;
			for (const [colName, total] of Object.entries(row.columnTotals ?? {})) {
				cells.push({
					quoteTableId: qId,
					valueColumnName: colName,
					total: total as number,
				});
			}
		}
		return {
			config: raw.config ?? {},
			cells,
			rowTotals,
			columnTotals: raw.columnTotals ?? {},
			grandTotal: raw.grandTotal ?? 0,
		};
	}

	return raw as SummaryTableData;
};

// Mirrors the 16 quote-mutation endpoints (PRO-687): the entity payload isn't
// consumed by any caller today, so — same as most of those 16 — this discards
// it and surfaces only the server-recomputed quote-tab validity. Repairing a
// dangling column reference by editing or deleting a summary table can flip
// the tab from invalid to valid; without this, the banner stayed stuck until
// an unrelated quote mutation or a page refresh happened to recompute it.
export const createSummaryTable = async (
	rfxId: number,
	payload: CreateSummaryTablePayload,
	resourceType: QuoteResourceType = "rfx"
): Promise<boolean | undefined> => {
	const response = await apiV2.post<ApiResponseWithAdditional<any>>(
		`${resourceType}/${rfxId}/summary-tables`,
		payload
	);
	return response.data.additional?.is_quote_tab_valid;
};

export const updateSummaryTable = async (
	rfxId: number,
	summaryTableId: number,
	payload: UpdateSummaryTablePayload,
	resourceType: QuoteResourceType = "rfx"
): Promise<boolean | undefined> => {
	const response = await apiV2.put<ApiResponseWithAdditional<any>>(
		`${resourceType}/${rfxId}/summary-tables/${summaryTableId}`,
		payload
	);
	return response.data.additional?.is_quote_tab_valid;
};

// This is the delete's own repair path: removing the table holding the
// dangling reference can flip validity false -> true, the opposite direction
// from create/update. Previously discarded the response entirely
// (`void` return, no captured body) — had to start capturing it.
export const deleteSummaryTable = async (
	rfxId: number,
	summaryTableId: number,
	resourceType: QuoteResourceType = "rfx"
): Promise<boolean | undefined> => {
	const response = await apiV2.delete<ApiResponseWithAdditional<void>>(
		`${resourceType}/${rfxId}/summary-tables/${summaryTableId}`
	);
	return response.data.additional?.is_quote_tab_valid;
};

export const validateSummaryTableValueColumns = async (
	rfxId: number,
	quoteTableIds: number[],
	valueColumnNames: string[],
	resourceType: QuoteResourceType = "rfx"
): Promise<{ valid: boolean; errors: string[] }> => {
	const response = await apiV2.post<{
		data: { valid: boolean; errors: string[] };
	}>(`${resourceType}/${rfxId}/summary-tables/validate`, {
		quote_ids: quoteTableIds,
		value_column_names: valueColumnNames,
	});
	return response.data.data;
};
